import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import AuthLayout from '../components/AuthLayout';
import PhoneAuthPanel from '../components/PhoneAuthPanel';
import { looksLikePhone, toE164Loose } from '../constants/phone';
import { takeReturnTo } from '../utils/returnTo';
import './auth.css';

function getFirebaseError(error) {
  const map = {
    'auth/user-not-found':         'No account found with this email address.',
    'auth/wrong-password':         'Incorrect password. Please try again.',
    'auth/invalid-credential':     'Invalid email or password.',
    'auth/invalid-email':          'Please enter a valid email address.',
    'auth/too-many-requests':      'Too many failed attempts. Try again later or reset your password.',
    'auth/popup-closed-by-user':   'Google sign-in was cancelled.',
    'auth/network-request-failed': 'Network error. Check your connection.',
    'auth/user-disabled':          'This account has been disabled.',
  };
  return map[error.code] || 'Something went wrong. Please try again.';
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path fill="#4285F4" d="M23.745 12.27c0-.79-.07-1.54-.19-2.27h-11.3v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
      <path fill="#34A853" d="M12.255 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96h-3.98v3.09C3.515 21.3 7.615 24 12.255 24z"/>
      <path fill="#FBBC05" d="M5.525 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62h-3.98a11.86 11.86 0 000 10.76l3.98-3.09z"/>
      <path fill="#EA4335" d="M12.255 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C18.205 1.19 15.495 0 12.255 0c-4.64 0-8.74 2.7-10.71 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"/>
    </svg>
  );
}

function EyeOpen() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeClosed() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

export default function Login() {
  const navigate                        = useNavigate();
  const location                        = useLocation();
  const { user, login, loginWithGoogle, loginWithPhonePassword } = useAuth();
  const { t }                           = useT();

  const [email,         setEmail]         = useState('');
  const [password,      setPassword]      = useState('');
  const [showPass,      setShowPass]      = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [toast,         setToast]         = useState(null);
  const [fieldErrors,   setFieldErrors]   = useState({});
  // Phone replaces the email form rather than sitting under it: they are two
  // ways of proving the same thing, and showing both at once asks the reader to
  // choose before they have read either.
  const [usePhone,      setUsePhone]      = useState(false);
  // What was typed in the combined field when it turned out to be a phone
  // number, handed to the panel so nobody types their number twice.
  const [phoneSeed,     setPhoneSeed]     = useState('');

  // ── ONE FIELD, EITHER CREDENTIAL ────────────────────────────────────────────
  // The top field takes an email address OR a phone number, because a reader
  // who signed up with a phone has no reason to know that this app files that
  // under a different button. Which one they are offering is inferred from what
  // they type, not asked.
  //
  // A PHONE NUMBER HAS TWO DOORS, so the form offers both rather than choosing
  // for the reader: type a password and it signs in with it; leave it empty and
  // it sends a code. The button says which one pressing it will do.
  //
  // The password half works because a phone account carries an email-shaped
  // alias with a linked password credential — Firebase has no phone-and-
  // password credential of its own. See constants/phone.js.
  const identifierIsPhone = looksLikePhone(email);
  // Only the FORGOT link is email-only: it sends a reset to a mailbox, and a
  // phone alias has none. Someone who has forgotten their password signs in
  // with a code instead, which is why the hint says so.
  const canResetByEmail = !identifierIsPhone;
  const phoneWithPassword = identifierIsPhone && password.length > 0;

  // WHERE A FINISHED SIGN-IN GOES. `state.from` is what ProtectedRoute passes;
  // the stored path is what the sign-up dialog on a public profile leaves
  // behind when someone takes its "already have an account?" link, which is a
  // route change and so has no state of its own to carry.
  //
  // Computed ONCE, in a lazy initialiser: reading it consumes it, and a plain
  // call in the render body would run on every render.
  const [from] = useState(() => location.state?.from?.pathname || takeReturnTo() || '/dashboard');

  // ── LEAVE THE ONE-STEP SIGN-INS ALONE ──────────────────────────────────────
  // This effect fires the instant `user` turns truthy, which for a Google
  // sign-in is DURING handleGoogle — before it can decide where a brand-new
  // account should go. It would win the race and send them to `from`, and the
  // chooser would then have to be reached by a second redirect from
  // ProtectedRoute, with the dashboard painted in between.
  //
  // PHONE IS THE SAME RACE. `confirmationResult.confirm()` signs the user in
  // and this effect sees it, while PhoneAuthPanel is still awaiting the
  // users/{uid} write that decides whether the account is new. One latch covers
  // both, because both are "signed in by something that is not this form".
  //
  // Left as-is for every other way of arriving already signed in, which is what
  // it is actually for.
  const oneStepFlow = useRef(false);
  useEffect(() => {
    if (user && !oneStepFlow.current) navigate(from, { replace: true });
  }, [user, navigate, from]);

  // Where a completed one-step sign-in goes. A new account has not been asked
  // customer-or-designer, so it goes to that page carrying its original
  // destination; a returning one resumes where it was headed.
  function finishOneStep({ isNewAccount }) {
    if (isNewAccount) {
      navigate('/account-type', { replace: true, state: { from: { pathname: from } } });
    } else {
      navigate(from, { replace: true });
    }
  }

  function showToast(msg, type = 'error') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }

  function validate() {
    const errors = {};
    if (!email.trim()) errors.email = t('auth_err_email_req');
    // Only an address is checked for the shape of an address. A phone number
    // is validated by the phone panel, which knows the rules per country.
    else if (!identifierIsPhone && !/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('auth_err_email_inv');
    }
    // For a phone number the password is OPTIONAL — leaving it blank is a
    // request for a code, not an omission.
    if (!identifierIsPhone && !password) errors.password = t('auth_err_pass_req');
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    // A phone number WITH a password: sign in against the number's alias.
    if (phoneWithPassword) {
      const e164 = toE164Loose(email.trim());
      if (!e164) { setFieldErrors({ email: t('phone_err_invalid') }); return; }
      setLoading(true);
      setToast(null);
      try {
        await loginWithPhonePassword(e164, password);
        navigate(from, { replace: true });
      } catch (err) {
        // The commonest failure here is not a wrong password but NO password:
        // an account created by code alone has never had one linked, and
        // Firebase reports that as an invalid credential. Saying "wrong
        // password" would send them to reset one that does not exist.
        showToast(
          err?.code === 'auth/invalid-credential' || err?.code === 'auth/user-not-found'
            ? t('auth_err_phone_no_password')
            : getFirebaseError(err),
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    // A phone number with no password: hand what was typed to the panel, which
    // sends the code immediately and takes over from the OTP step onwards.
    if (identifierIsPhone) {
      setToast(null);
      setPhoneSeed(email.trim());
      setUsePhone(true);
      return;
    }

    setLoading(true);
    setToast(null);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      showToast(getFirebaseError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setGoogleLoading(true);
    setToast(null);
    oneStepFlow.current = true;
    try {
      // SIGNING IN CAN ALSO BE SIGNING UP: there is one Google button and it
      // creates the account if there is none. A new one has not been asked
      // customer-or-designer, so it goes to that page — carrying where it was
      // headed, so answering finishes the journey instead of ending it on the
      // dashboard.
      const { isNewAccount } = await loginWithGoogle();
      finishOneStep({ isNewAccount });
    } catch (err) {
      // The sign-in never happened, so the effect above must go back to being
      // the thing that redirects an already-signed-in visitor.
      oneStepFlow.current = false;
      showToast(getFirebaseError(err));
    } finally {
      setGoogleLoading(false);
    }
  }

  const busy = loading || googleLoading;

  return (
    <AuthLayout>
      <div>
        {/* Header */}
        <div className="af-header">
          <h2 className="af-title">{t('auth_login_title')}</h2>
          <p className="af-subtitle">{t('auth_login_sub')}</p>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`af-toast af-toast--${toast.type}`} role="alert">
            <span className="af-toast__icon">{toast.type === 'error' ? '⚠' : '✓'}</span>
            {toast.msg}
          </div>
        )}

        {/* Google */}
        <button className="af-google-btn" onClick={handleGoogle} disabled={busy} type="button">
          {googleLoading ? <span className="af-spinner" /> : <GoogleIcon />}
          {t('auth_google_signin')}
        </button>

        {/* Phone — the other one-step sign-in. Sits with Google because it
            behaves like it: no password, and it creates the account if there
            is none. */}
        <button
          className="af-google-btn af-phone-btn"
          onClick={() => { setUsePhone(true); setToast(null); }}
          disabled={busy || usePhone}
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="none" width="19" height="19" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="6" y="2" width="12" height="20" rx="2.5" />
            <path d="M11 18.5h2" />
          </svg>
          {t('auth_phone_signin')}
        </button>

        {/* Divider */}
        <div className="af-divider">
          {usePhone ? t('auth_or_phone_signin') : t('auth_or_email_signin')}
        </div>

        {usePhone ? (
          <PhoneAuthPanel
            onDone={finishOneStep}
            onCancel={() => { setUsePhone(false); setPhoneSeed(''); }}
            // Prefilled from the field above when a number was typed there, and
            // sent straight away — the reader has already given the number
            // once, so asking again would be the app not having listened.
            initialNumber={phoneSeed}
            autoSend={!!phoneSeed}
            // The panel signs the user in itself, so the redirect effect has to
            // stand down for the same reason it does during the Google popup.
            onStart={() => { oneStepFlow.current = true; }}
          />
        ) : (
        <form onSubmit={handleSubmit} noValidate>

          {/* Email OR phone — see identifierIsPhone above. */}
          <div className="af-field">
            <label className="af-label" htmlFor="login-email">{t('auth_id_label')}</label>
            <div className="af-input-wrap">
              {/* The icon follows what is being typed, so the form shows it has
                  understood before the reader has to press anything. */}
              <span className="af-input-icon">
                {identifierIsPhone ? (
                  <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                )}
              </span>
              <input
                id="login-email"
                className={`af-input${fieldErrors.email ? ' af-input--error' : ''}`}
                // `text`, not `email`: type="email" makes the browser refuse to
                // submit a phone number and show its own "include an @" bubble,
                // over a field that now legitimately accepts one.
                type="text"
                // The keypad still follows the content, so a phone number is
                // typed on a number pad and an address on a keyboard.
                inputMode={identifierIsPhone ? 'tel' : 'email'}
                placeholder={t('auth_id_ph')}
                value={email}
                onChange={e => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: '' })); }}
                // `username` rather than `email`, so a password manager offers
                // whichever identifier it has stored for this site.
                autoComplete="username"
                disabled={busy}
              />
            </div>
            {fieldErrors.email && <p className="af-field-error">{fieldErrors.email}</p>}
          </div>

          {/* Password — shown for BOTH credentials. Optional for a phone
              number: filled in, it signs in against that number's alias; left
              empty, the button sends a code instead. */}
          <div className="af-field">
            <label className="af-label" htmlFor="login-password">
              {t('auth_password_label')}
              {/* Says it is optional HERE, next to the field, rather than
                  leaving the reader to discover it by pressing the button. */}
              {identifierIsPhone && <span className="dpf-opt"> {t('ds_optional')}</span>}
            </label>
            <div className="af-input-wrap">
              <span className="af-input-icon">
                <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
              </span>
              <input
                id="login-password"
                className={`af-input af-input--has-action${fieldErrors.password ? ' af-input--error' : ''}`}
                type={showPass ? 'text' : 'password'}
                placeholder={t('auth_password_ph')}
                value={password}
                onChange={e => { setPassword(e.target.value); setFieldErrors(p => ({ ...p, password: '' })); }}
                autoComplete="current-password"
                disabled={busy}
              />
              <button type="button" className="af-input-action" onClick={() => setShowPass(s => !s)} tabIndex={-1}>
                {showPass ? <EyeClosed /> : <EyeOpen />}
              </button>
            </div>
            {fieldErrors.password && <p className="af-field-error">{fieldErrors.password}</p>}
          </div>

          {/* EMAIL ONLY. A reset is sent to a mailbox, and a phone alias has
              none — someone who has forgotten their password signs in with a
              code instead, which the hint below says. */}
          {canResetByEmail && (
            <Link to="/forgot-password" className="af-forgot">{t('auth_forgot')}</Link>
          )}

          {/* Says what pressing it does. With a phone number and no password
              that is sending an SMS, not signing in, and a button labelled
              "Sign in" would be promising the wrong thing. */}
          <button className="af-btn" type="submit" disabled={busy}>
            {loading && <span className="af-spinner" />}
            {loading ? t('auth_signin_ing')
              : identifierIsPhone && !phoneWithPassword ? t('phone_send')
                : t('auth_signin_btn')}
          </button>

          {identifierIsPhone && (
            <p className="pa-legal lg-id-hint">
              {phoneWithPassword ? t('auth_id_phone_pw_hint') : t('auth_id_phone_hint')}
            </p>
          )}
        </form>
        )}

        <p className="af-footer">
          {t('auth_no_account')}{' '}
          <Link to="/register">{t('auth_create_free')}</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
